import Phaser from 'phaser';
import { TILE_SIZE, GAME_WIDTH, GAME_HEIGHT } from '../game/constants';
import { runState } from '../services/RunState';
import { profileStore } from '../services/ProfileStore';
import { gameBridge } from '../services/GameBridge';
import { WORLD_TILES, MONSTERS, randomElement, getCharacterOption } from '../config/tileIndex';
import {
  resolveDungeonVisuals,
  resolvePropConfig,
  type ResolvedDungeonVisuals,
} from '../config/oryxThemes';
import {
  addHalo,
  computeCameraZoom,
  getPaletteForSubject,
  pickDecalFrame,
  pickFloorFrame,
  pickWallFrame,
  shouldPlaceDecal,
  shouldPlaceTorch,
  type TilePalette,
} from '../services/TilePalette';

/**
 * Dungeon JSON structure from generated dungeons
 */
interface DungeonData {
  [key: string]: unknown;
  id: string;
  name: string;
  subject: string;
  version: number;
  width: number;
  height: number;
  theme: {
    floor: string;   // 'wood', 'stone', 'tile', 'carpet', etc.
    wall: string;    // 'stone', 'cave', 'crypt', 'sewer'
    props: string[]; // ['bookshelf', 'table', 'candles', 'chair']
    monster: string; // 'wizard', 'zombie', 'spider', etc.
    boss: string;    // 'necromancer', 'dragon', etc.
  };
  layers: {
    floor: number[];  // Flat array: 0 = void, 1 = floor
    walls: number[];  // Flat array: 0 = empty, 1 = wall
    props: { x: number; y: number; type: string }[];
  };
  spawn: { x: number; y: number };
  exit: { x: number; y: number };
  encounters: {
    id: string;
    x: number;
    y: number;
    difficulty: 'easy' | 'medium' | 'hard';
    cleared: boolean;
  }[];
  boss: {
    x: number;
    y: number;
    type: string;
    requiredClears: number;
  };
}

interface EncounterZone {
  zone: Phaser.GameObjects.Zone;
  encounter: DungeonData['encounters'][0];
  monster: Phaser.GameObjects.Sprite;
  cleared: boolean;
}

interface DungeonSceneData {
  subject: string;
  dungeonId: string;
}

const SCALE = 2;
const SCALED_TILE = TILE_SIZE * SCALE;

/**
 * DungeonScene - Procedural dungeon exploration with encounters
 */
export class DungeonScene extends Phaser.Scene {
  private subject!: string;
  private dungeonId!: string;
  private dungeonData!: DungeonData;
  private charId!: string;

  // Game objects
  private player!: Phaser.GameObjects.Sprite;
  private walls!: Phaser.Physics.Arcade.StaticGroup;
  private encounterZones: EncounterZone[] = [];
  private exitZone!: Phaser.GameObjects.Zone;
  private exitSprite?: Phaser.GameObjects.Sprite;
  private bossZone?: Phaser.GameObjects.Zone;
  private bossMonster?: Phaser.GameObjects.Sprite;
  private propOccupied: Set<string> = new Set();
  private npcZones: { zone: Phaser.GameObjects.Zone; sprite: Phaser.GameObjects.Sprite; type: 'merchant' | 'healer' }[] = [];

  // Input
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasdKeys!: Record<'W' | 'A' | 'S' | 'D', Phaser.Input.Keyboard.Key>;
  private playerSpeed = 160;
  private lastDirection: 'up' | 'down' | 'left' | 'right' = 'down';

  // State
  private clearedCount = 0;
  private canInteract = true;
  private isPaused = false;

  // Map positioning
  private offsetX = 0;
  private offsetY = 0;

  // Resolved visual theme
  private visuals!: ResolvedDungeonVisuals;
  private pauseOverlay?: Phaser.GameObjects.Container;
  private palette!: TilePalette;
  private cameraZoom = 1;
  private bossGateReady = false;

  constructor() {
    super({ key: 'DungeonScene' });
  }

  init(data: DungeonSceneData): void {
    this.subject = data.subject;
    this.dungeonId = data.dungeonId;
    this.clearedCount = 0;
    this.canInteract = true;
    this.encounterZones = [];
    this.propOccupied.clear();
  }

  create(): void {
    // Load dungeon data from cache
    const dungeonKey = `dungeon-${this.dungeonId}`;
    this.dungeonData = this.cache.json.get(dungeonKey);

    if (!this.dungeonData) {
      console.error(`Dungeon data not found: ${dungeonKey}`);
      this.returnToHub();
      return;
    }

    this.palette = getPaletteForSubject(this.subject);

    // Resolve theme strings to actual tile frames
    this.visuals = resolveDungeonVisuals(this.dungeonData);

    // Calculate map offset to center
    const mapPixelWidth = this.dungeonData.width * SCALED_TILE;
    const mapPixelHeight = this.dungeonData.height * SCALED_TILE;
    this.offsetX = (GAME_WIDTH - mapPixelWidth) / 2;
    this.offsetY = (GAME_HEIGHT - mapPixelHeight) / 2;

    this.cameraZoom = computeCameraZoom(
      this.dungeonData.width,
      this.dungeonData.height,
      SCALED_TILE,
      GAME_WIDTH,
      GAME_HEIGHT
    );

    // Create wall collision group
    this.walls = this.physics.add.staticGroup();

    // Build dungeon layers
    this.createFloorLayer();
    this.createWallLayer();
    this.createPropsLayer();
    this.createEncounters();
    this.createExit();
    this.createBoss();
    this.createPlayer();
    this.createNPCs();
    this.setupInput();
    this.setupCollisions();
    this.setupCamera();
    this.createUI();

    // Start a run for this dungeon
    runState.startRun(this.subject);

    // Emit scene change event
    gameBridge.emit('scene-changed', { scene: 'dungeon' });
    gameBridge.emit('dungeon-entered', {
      dungeonId: this.dungeonId,
      subject: this.subject,
      encounterCount: this.dungeonData.encounters.length,
    });

    // Play dungeon music only if not already playing (prevents conflict with WorldScene)
    if (profileStore.isAudioEnabled() && this.sound.get('courtroom') && !this.sound.get('courtroom')?.isPlaying) {
      this.sound.play('courtroom', { loop: true, volume: 0.2 });
    }

    // Handle encounter completion when waking up
    this.events.on('wake', (_sys: Phaser.Scenes.Systems, data: { wasCorrect: boolean }) => {
      this.onReturnFromEncounter(data);
    });

    // Expose E2E helpers to allow deterministic encounters in tests
    if ((window as any).__E2E_TEST_MODE) {
      this.time.delayedCall(200, () => {
        (window as any).__TEST_DUNGEON_ID = this.dungeonId;
        (window as any).__TEST_DUNGEON_ENCOUNTERS = this.encounterZones.map((ez, i) => ({
          index: i,
          worldX: ez.zone.x,
          worldY: ez.zone.y,
          screenX: Math.round(ez.zone.x - this.cameras.main.scrollX),
          screenY: Math.round(ez.zone.y - this.cameras.main.scrollY),
        }));
        (window as any).__TEST_FIRST_ENCOUNTER_INDEX = this.encounterZones.length > 0 ? 0 : -1;

        // Start an encounter by index (used by E2E tests)
        (window as any).__startEncounterIndex = (i: number) => {
          if (!this.encounterZones[i]) return false;
          this.onEncounterOverlap(this.encounterZones[i]);
          return true;
        };
      });
    }
  }

  private onReturnFromEncounter(data: { wasCorrect: boolean }): void {
    // Emit scene change to tell React to hide EncounterUI
    gameBridge.emit('scene-changed', { scene: 'dungeon' });

    // Mark current encounter as cleared if it was correct
    const currentEncounter = this.data.get('currentEncounter') as EncounterZone | null;
    if (currentEncounter && data?.wasCorrect) {
      if (!currentEncounter.cleared) {
        currentEncounter.cleared = true;
        this.clearedCount++;
      }
      // Fade out the monster sprite
      if (currentEncounter.monster) {
        this.tweens.add({
          targets: currentEncounter.monster,
          alpha: 0,
          scale: 0,
          duration: 300,
          onComplete: () => currentEncounter.monster.destroy()
        });
      }
    }
    this.updateProgressUI();

    // Always re-enable interaction and restore player
    this.time.delayedCall(100, () => {
      this.canInteract = true;
      this.player.setAlpha(1);
    });
  }

  private createFloorLayer(): void {
    const { width, height, layers } = this.dungeonData;
    const floorTiles = this.visuals.floorTiles;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const index = y * width + x;
        if (layers.floor[index] === 1) {
          const floorFrame = pickFloorFrame(x, y, width, height, this.palette);
          const sprite = this.add.sprite(
            this.offsetX + x * SCALED_TILE + SCALED_TILE / 2,
            this.offsetY + y * SCALED_TILE + SCALED_TILE / 2,
            'world',
            floorFrame || randomElement(floorTiles)
          );
          sprite.setScale(SCALE);
          sprite.setDepth(0);

          if (shouldPlaceDecal(this.palette)) {
            const decal = this.add.sprite(
              sprite.x,
              sprite.y,
              'world',
              pickDecalFrame(this.palette)
            );
            decal.setScale(SCALE);
            decal.setDepth(1);
            decal.setAlpha(0.8);
          }
        }
      }
    }

    // Ambient overlay for atmosphere
    if (this.palette.ambientColor) {
      const overlay = this.add.rectangle(
        GAME_WIDTH / 2,
        GAME_HEIGHT / 2,
        GAME_WIDTH,
        GAME_HEIGHT,
        this.palette.ambientColor,
        0.12
      );
      overlay.setDepth(2).setScrollFactor(0);
    }
  }

  private createWallLayer(): void {
    const { width, height, layers } = this.dungeonData;
    const wallTilesH = this.visuals.wallTilesH;
    const wallTilesV = this.visuals.wallTilesV;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const index = y * width + x;
        if (layers.walls[index] === 1) {
          // Determine wall orientation based on position
          const isTopOrBottom = y === 0 || y === height - 1;
          const wallTile = pickWallFrame(isTopOrBottom, this.palette) || (isTopOrBottom
            ? randomElement(wallTilesH)
            : randomElement(wallTilesV));

          const sprite = this.add.sprite(
            this.offsetX + x * SCALED_TILE + SCALED_TILE / 2,
            this.offsetY + y * SCALED_TILE + SCALED_TILE / 2,
            'world',
            wallTile
          );
          sprite.setScale(SCALE);
          sprite.setDepth(5);

          // Add collision
          const wallRect = this.add.rectangle(
            this.offsetX + x * SCALED_TILE + SCALED_TILE / 2,
            this.offsetY + y * SCALED_TILE + SCALED_TILE / 2,
            SCALED_TILE,
            SCALED_TILE,
            0x000000,
            0
          );
          this.physics.add.existing(wallRect, true);
          this.walls.add(wallRect);

          // Auto-place torches on walls at intervals if space is free
          const key = `${x},${y}`;
          if (shouldPlaceTorch(x, y, this.palette) && !this.propOccupied.has(key)) {
            const torch = this.add.sprite(
              sprite.x,
              sprite.y - SCALED_TILE / 4,
              'world',
              WORLD_TILES.TORCH_1
            );
            torch.setScale(SCALE);
            torch.setDepth(6);
            torch.play('torch-flicker');
            addHalo(this, sprite.x, sprite.y - SCALED_TILE / 4, this.palette.haloColor ?? 0xffdd88, 26, 0.2);
            this.propOccupied.add(key);
          }
        }
      }
    }
  }

  private createPropsLayer(): void {
    const { layers } = this.dungeonData;

    for (const prop of layers.props) {
      const propConfig = resolvePropConfig(prop.type);
      if (!propConfig) continue;
      const key = `${prop.x},${prop.y}`;
      this.propOccupied.add(key);

      const sprite = this.add.sprite(
        this.offsetX + prop.x * SCALED_TILE + SCALED_TILE / 2,
        this.offsetY + prop.y * SCALED_TILE + SCALED_TILE / 2,
        'world',
        propConfig.frame
      );
      sprite.setScale(SCALE);
      sprite.setDepth(3);

      if (propConfig.animationKey) {
        sprite.play(propConfig.animationKey);
        addHalo(
          this,
          sprite.x,
          sprite.y,
          this.palette.haloColor ?? 0xffdd88,
          SCALED_TILE * 0.9,
          0.18
        );
      }
    }
  }

  private createEncounters(): void {
    const { encounters } = this.dungeonData;
    const monsterFrame = this.visuals.monsterFrame;

    for (const encounter of encounters) {
      // Create monster sprite
      const monster = this.add.sprite(
        this.offsetX + encounter.x * SCALED_TILE + SCALED_TILE / 2,
        this.offsetY + encounter.y * SCALED_TILE + SCALED_TILE / 2,
        'monsters',
        monsterFrame
      );
      monster.setScale(SCALE);
      monster.setDepth(10);

      // Difficulty-based tinting
      if (encounter.difficulty === 'hard') {
        monster.setTint(0xff4444);
      } else if (encounter.difficulty === 'medium') {
        monster.setTint(0xffaa44);
      }

      // Idle bobbing animation
      this.tweens.add({
        targets: monster,
        y: monster.y - 6,
        duration: 1000 + Math.random() * 500,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });

      // Create interaction zone (larger for easier interaction)
      const zone = this.add.zone(
        this.offsetX + encounter.x * SCALED_TILE + SCALED_TILE / 2,
        this.offsetY + encounter.y * SCALED_TILE + SCALED_TILE / 2,
        SCALED_TILE * 2.5,
        SCALED_TILE * 2.5
      );
      this.physics.add.existing(zone);
      (zone.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);

      this.encounterZones.push({
        zone,
        encounter,
        monster,
        cleared: encounter.cleared,
      });
    }
  }

  private createExit(): void {
    const { exit } = this.dungeonData;

    // Create exit door sprite
    this.exitSprite = this.add.sprite(
      this.offsetX + exit.x * SCALED_TILE + SCALED_TILE / 2,
      this.offsetY + exit.y * SCALED_TILE + SCALED_TILE / 2,
      'world',
      WORLD_TILES.WALL_STONE_STAIR_UP
    );
    this.exitSprite.setScale(SCALE);
    this.exitSprite.setDepth(2);
    this.exitSprite.setTint(0x44ff44);

    // Gentle pulse to highlight the exit
    this.tweens.add({
      targets: this.exitSprite,
      alpha: 0.8,
      yoyo: true,
      duration: 900,
      repeat: -1,
    });

    // Create exit zone (larger for easier interaction)
    this.exitZone = this.add.zone(
      this.offsetX + exit.x * SCALED_TILE + SCALED_TILE / 2,
      this.offsetY + exit.y * SCALED_TILE + SCALED_TILE / 2,
      SCALED_TILE * 2.5,
      SCALED_TILE * 2.5
    );
    this.physics.add.existing(this.exitZone);
    (this.exitZone.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);
  }

  private createBoss(): void {
    const { boss } = this.dungeonData;
    const bossFrame = this.visuals?.bossFrame ?? 0;

    // Create boss monster (larger)
    this.bossMonster = this.add.sprite(
      this.offsetX + boss.x * SCALED_TILE + SCALED_TILE / 2,
      this.offsetY + boss.y * SCALED_TILE + SCALED_TILE / 2,
      'monsters',
      bossFrame
    );
    this.bossMonster.setScale(SCALE * 1.5);
    this.bossMonster.setDepth(10);
    this.bossMonster.setTint(0xff00ff);
    addHalo(
      this,
      this.offsetX + boss.x * SCALED_TILE + SCALED_TILE / 2,
      this.offsetY + boss.y * SCALED_TILE + SCALED_TILE / 2,
      this.palette.haloColor ?? 0xff55ff,
      SCALED_TILE * 1.2,
      0.22
    );

    // Banner props flanking the boss for emphasis
    const bannerOffsets = [-1, 1];
    for (const offset of bannerOffsets) {
      const key = `${boss.x + offset},${boss.y}`;
      if (!this.propOccupied.has(key)) {
        const banner = this.add.sprite(
          this.offsetX + (boss.x + offset) * SCALED_TILE + SCALED_TILE / 2,
          this.offsetY + boss.y * SCALED_TILE + SCALED_TILE / 2,
          'world',
          WORLD_TILES.BANNER_A
        );
        banner.setScale(SCALE);
        banner.setDepth(4);
        this.propOccupied.add(key);
      }
    }

    // Boss idle animation
    this.tweens.add({
      targets: this.bossMonster,
      y: this.bossMonster.y - 8,
      scaleX: SCALE * 1.6,
      scaleY: SCALE * 1.4,
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Create boss zone (larger for easier interaction)
    this.bossZone = this.add.zone(
      this.offsetX + boss.x * SCALED_TILE + SCALED_TILE / 2,
      this.offsetY + boss.y * SCALED_TILE + SCALED_TILE / 2,
      SCALED_TILE * 3,
      SCALED_TILE * 3
    );
    this.physics.add.existing(this.bossZone);
    (this.bossZone.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);

    // Place a legal-themed altar to unlock the boss fight
    const altarKey = `${boss.x - 1},${boss.y}`;
    if (!this.propOccupied.has(altarKey)) {
      const altar = this.add.sprite(
        this.offsetX + (boss.x - 1) * SCALED_TILE + SCALED_TILE / 2,
        this.offsetY + boss.y * SCALED_TILE + SCALED_TILE / 2,
        'world',
        WORLD_TILES.ALTAR
      );
      altar.setScale(SCALE);
      altar.setDepth(4);
      this.propOccupied.add(altarKey);

      const altarZone = this.add.zone(altar.x, altar.y, SCALED_TILE * 1.5, SCALED_TILE * 1.5);
      this.physics.add.existing(altarZone);
      (altarZone.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);

      this.physics.add.overlap(this.player, altarZone, () => {
        if (this.bossGateReady) return;
        this.bossGateReady = true;
        this.showMessage('Motion granted: boss is now on the docket!');
        if (this.bossMonster) {
          this.tweens.add({
            targets: this.bossMonster,
            scaleX: this.bossMonster.scale * 1.1,
            scaleY: this.bossMonster.scale * 1.1,
            yoyo: true,
            duration: 300,
          });
        }
        altar.setTint(0x55ff55);
      });
    }
  }

  private createNPCs(): void {
    const { spawn } = this.dungeonData;
    const placements = [
      { x: spawn.x + 2, y: spawn.y, type: 'merchant' as const },
      { x: spawn.x - 2, y: spawn.y + 2, type: 'healer' as const },
    ];

    for (const npc of placements) {
      const key = `${npc.x},${npc.y}`;
      if (this.propOccupied.has(key)) continue;
      const sprite = this.add.sprite(
        this.offsetX + npc.x * SCALED_TILE + SCALED_TILE / 2,
        this.offsetY + npc.y * SCALED_TILE + SCALED_TILE / 2,
        npc.type === 'merchant' ? 'monsters' : 'monsters',
        npc.type === 'merchant' ? this.visuals.monsterFrame : MONSTERS.CLERIC
      );
      sprite.setScale(SCALE * 1.1);
      sprite.setDepth(8);
      this.tweens.add({
        targets: sprite,
        y: sprite.y - 4,
        duration: 1000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });

      const zone = this.add.zone(sprite.x, sprite.y, SCALED_TILE * 1.8, SCALED_TILE * 1.8);
      this.physics.add.existing(zone);
      (zone.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);

      this.physics.add.overlap(this.player, zone, () => {
        if (!this.canInteract) return;
        if (npc.type === 'merchant') {
          const price = 40;
          if (profileStore.spendCoins(price)) {
            profileStore.addXP(75);
            this.showMessage('Law Librarian sold a case brief: +75 XP');
          } else {
            this.showMessage('Not enough fees for the Law Librarian (40 coins)');
          }
        } else {
          profileStore.addXP(30);
          this.showMessage('Court Clerk filed a continuance: +30 XP');
        }
      });

      this.npcZones.push({ zone, sprite, type: npc.type });
      this.propOccupied.add(key);
    }
  }

  private createPlayer(): void {
    const { spawn } = this.dungeonData;
    this.charId = profileStore.getSelectedCharacter();
    const charOption = getCharacterOption(this.charId);
    const isHighResAtlas = charOption.texture.startsWith('k1_kim');

    if (isHighResAtlas) {
      // Kim uses high-res atlas (192x272 frames)
      const firstFrame = `${charOption.texture}_0001`;
      this.player = this.add.sprite(
        this.offsetX + spawn.x * SCALED_TILE + SCALED_TILE / 2,
        this.offsetY + spawn.y * SCALED_TILE + SCALED_TILE / 2,
        charOption.texture,
        firstFrame
      );
      // Scale down 192x272 to roughly 32x45 (similar to SCALE for 16x16)
      this.player.setScale(0.2);
    } else {
      this.player = this.add.sprite(
        this.offsetX + spawn.x * SCALED_TILE + SCALED_TILE / 2,
        this.offsetY + spawn.y * SCALED_TILE + SCALED_TILE / 2,
        charOption.texture || 'monsters',
        charOption.frame
      );
      this.player.setScale(SCALE);
    }
    this.player.setDepth(15);

    // Play idle animation
    const idleAnim = `${charOption.id}-idle`;
    if (this.anims.exists(idleAnim)) {
      this.player.play(idleAnim);
    }

    this.physics.add.existing(this.player);
    const body = this.player.body as Phaser.Physics.Arcade.Body;
    body.setCollideWorldBounds(true);
    // Scale hitbox to match scaled sprite
    body.setSize(TILE_SIZE * SCALE * 0.75, TILE_SIZE * SCALE * 0.75);
    body.setOffset(TILE_SIZE * SCALE * 0.125, TILE_SIZE * SCALE * 0.125);

    // Entrance animation
    this.player.setAlpha(0);
    this.tweens.add({
      targets: this.player,
      alpha: 1,
      duration: 300,
      ease: 'Power2',
    });
  }

  private setupInput(): void {
    this.cursors = this.input.keyboard!.createCursorKeys();
    this.wasdKeys = this.input.keyboard!.addKeys('W,A,S,D') as Record<
      'W' | 'A' | 'S' | 'D',
      Phaser.Input.Keyboard.Key
    >;

    // ESC to return to hub
    this.input.keyboard!.on('keydown-ESC', () => {
      this.returnToHub();
    });

    // P to toggle pause/settings overlay
    this.input.keyboard!.on('keydown-P', () => {
      this.togglePause();
    });
  }

  private setupCamera(): void {
    const { width, height } = this.dungeonData;

    // Calculate map bounds in world coordinates
    const mapWidth = width * SCALED_TILE;
    const mapHeight = height * SCALED_TILE;

    // Set physics world bounds to match the map (so player can move to edges)
    this.physics.world.setBounds(this.offsetX, this.offsetY, mapWidth, mapHeight);

    // Set camera bounds to the map area
    this.cameras.main.setBounds(
      this.offsetX,
      this.offsetY,
      mapWidth,
      mapHeight
    );
    this.cameras.main.setZoom(this.cameraZoom);

    // Make camera follow the player
    this.cameras.main.startFollow(this.player, true, 0.1, 0.1);

    // Set a dead zone so camera doesn't jitter on small movements
    this.cameras.main.setDeadzone(80, 60);
  }

  private setupCollisions(): void {
    // Player-wall collision
    this.physics.add.collider(this.player, this.walls);

    // Encounter zone overlaps
    for (const encounterZone of this.encounterZones) {
      this.physics.add.overlap(
        this.player,
        encounterZone.zone,
        () => this.onEncounterOverlap(encounterZone),
        undefined,
        this
      );
    }

    // Exit zone overlap
    this.physics.add.overlap(
      this.player,
      this.exitZone,
      () => this.onExitOverlap(),
      undefined,
      this
    );

    // Boss zone overlap
    if (this.bossZone) {
      this.physics.add.overlap(
        this.player,
        this.bossZone,
        () => this.onBossOverlap(),
        undefined,
        this
      );
    }
  }

  private createUI(): void {
    // Dungeon name - fixed on screen (below HUD)
    this.add.text(GAME_WIDTH / 2, 55, this.dungeonData.name, {
      fontSize: '14px',
      color: '#ffcc00',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(100).setScrollFactor(0);

    // Instructions - fixed on screen
    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 15, 'Arrow keys to move. Touch monsters to battle. ESC to flee.', {
      fontSize: '10px',
      color: '#aaaaaa',
      fontFamily: 'Arial',
    }).setOrigin(0.5).setDepth(100).setScrollFactor(0);

    // Progress indicator - fixed on screen
    this.add.text(10, 55, this.getProgressLabel(), {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setName('progress').setDepth(100).setScrollFactor(0);
  }

  private updateProgressUI(): void {
    const progressText = this.children.getByName('progress') as Phaser.GameObjects.Text;
    if (progressText) {
      progressText.setText(this.getProgressLabel());
    }
  }

  private getProgressLabel(): string {
    return `Encounters: ${this.clearedCount}/${this.dungeonData.encounters.length} (Boss at ${this.dungeonData.boss.requiredClears})`;
  }

  private onEncounterOverlap(encounterZone: EncounterZone): void {
    if (!this.canInteract || encounterZone.cleared) return;

    this.canInteract = false;

    // Get a card for this encounter via run queue
    const card = runState.getNextCard();
    if (!card) {
      console.error('No cards available for encounter');
      this.canInteract = true;
      return;
    }

    // Store encounter reference for when we return
    this.data.set('currentEncounter', encounterZone);

    // Transition to encounter
    this.tweens.add({
      targets: this.player,
      alpha: 0,
      duration: 200,
      onComplete: () => {
        // Sleep dungeon scene and start encounter on top
        this.scene.sleep('DungeonScene');
        this.scene.run('EncounterScene', {
          cardId: card.id,
          subject: this.subject,
          isRematch: false,
          dungeonId: this.dungeonId,
          encounterIndex: this.dungeonData.encounters.indexOf(encounterZone.encounter),
        });
      },
    });
  }

  private onExitOverlap(): void {
    if (!this.canInteract) return;

    // Check if enough encounters cleared
    if (this.clearedCount < this.dungeonData.boss.requiredClears) {
      // Show message
      this.showMessage(`Clear ${this.dungeonData.boss.requiredClears - this.clearedCount} more encounters to exit!`);
      return;
    }

    this.canInteract = false;

    if (this.exitSprite) {
      this.tweens.add({
        targets: this.exitSprite,
        scaleX: SCALE * 1.2,
        scaleY: SCALE * 1.2,
        duration: 200,
        yoyo: true,
      });
    }

    // Victory! Complete dungeon
    gameBridge.emit('dungeon-completed', {
      dungeonId: this.dungeonId,
      subject: this.subject,
      clearedCount: this.clearedCount,
    });

    this.tweens.add({
      targets: this.player,
      alpha: 0,
      y: this.player.y - 50,
      duration: 500,
      onComplete: () => {
        this.returnToHub();
      },
    });
  }

  private onBossOverlap(): void {
    if (!this.canInteract) return;

    if (!this.bossGateReady) {
      this.showMessage('File the motion at the altar before arguing the boss!');
      return;
    }

    // Check if enough encounters cleared for boss
    if (this.clearedCount < this.dungeonData.boss.requiredClears) {
      this.showMessage(`Defeat ${this.dungeonData.boss.requiredClears - this.clearedCount} more monsters first!`);
      return;
    }

    this.canInteract = false;

    // Get a hard card for boss
    const card = runState.getNextCard();
    if (!card) {
      this.canInteract = true;
      return;
    }

    // Transition to boss encounter
    this.tweens.add({
      targets: this.player,
      alpha: 0,
      duration: 200,
      onComplete: () => {
        this.data.set('isBossEncounter', true);
        this.scene.sleep('DungeonScene');
        this.scene.run('EncounterScene', {
          cardId: card.id,
          subject: this.subject,
          isRematch: false,
          dungeonId: this.dungeonId,
          isBoss: true,
        });
      },
    });
  }

  private showMessage(text: string): void {
    const msg = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, text, {
      fontSize: '14px',
      color: '#ffff00',
      fontFamily: 'Arial',
      backgroundColor: '#000000cc',
      padding: { x: 10, y: 5 },
    }).setOrigin(0.5).setDepth(200).setScrollFactor(0);

    this.tweens.add({
      targets: msg,
      alpha: 0,
      y: msg.y - 30,
      duration: 2000,
      onComplete: () => msg.destroy(),
    });
  }

  private returnToHub(): void {
    runState.endRun();
    gameBridge.emit('scene-changed', { scene: 'hub' });
    this.scene.start('WorldScene');
  }

  private togglePause(): void {
    this.isPaused = !this.isPaused;
    if (this.isPaused) {
      this.canInteract = false;
      this.physics.world.timeScale = 0;
      const overlay = this.add.container(0, 0);
      const bg = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6);
      const text = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'Paused\nP to resume\nESC to hub', {
        fontSize: '14px',
        color: '#ffffff',
        fontFamily: 'Arial',
        align: 'center',
      }).setOrigin(0.5);
      overlay.add([bg, text]);
      overlay.setDepth(400).setScrollFactor(0);
      this.pauseOverlay = overlay;
    } else {
      this.canInteract = true;
      this.physics.world.timeScale = 1;
      this.pauseOverlay?.destroy();
      this.pauseOverlay = undefined;
    }
  }

  /**
   * Called when returning from an encounter
   */
  handleEncounterResult(wasCorrect: boolean): void {
    const encounterZone = this.data.get('currentEncounter') as EncounterZone | undefined;
    const isBoss = this.data.get('isBossEncounter') as boolean;

    this.data.remove('currentEncounter');
    this.data.remove('isBossEncounter');

    if (wasCorrect) {
      if (isBoss && this.bossMonster) {
        // Boss defeated - victory animation
        this.tweens.add({
          targets: this.bossMonster,
          scaleX: 0,
          scaleY: 0,
          alpha: 0,
          duration: 500,
          onComplete: () => {
            this.bossMonster?.destroy();
            this.bossZone?.destroy();
          },
        });
        this.showMessage('BOSS DEFEATED!');
      } else if (encounterZone) {
        // Regular encounter cleared
        encounterZone.cleared = true;
        this.clearedCount++;
        this.updateProgressUI();

        // Defeat animation
        this.tweens.add({
          targets: encounterZone.monster,
          scaleX: 0,
          scaleY: 0,
          alpha: 0,
          duration: 300,
          onComplete: () => {
            encounterZone.monster.destroy();
            encounterZone.zone.destroy();
          },
        });
      }
    }

    // Re-enable interaction
    this.time.delayedCall(500, () => {
      this.canInteract = true;
      this.player.setAlpha(1);
    });
  }

  update(): void {
    if (this.isPaused || !this.player || !this.player.body) return;

    const body = this.player.body as Phaser.Physics.Arcade.Body;
    const keys = this.wasdKeys;

    body.setVelocity(0);

    let isMoving = false;
    let newDirection: 'up' | 'down' | 'left' | 'right' = this.lastDirection;

    if (this.cursors.left.isDown || keys.A.isDown) {
      body.setVelocityX(-this.playerSpeed);
      newDirection = 'left';
      isMoving = true;
    } else if (this.cursors.right.isDown || keys.D.isDown) {
      body.setVelocityX(this.playerSpeed);
      newDirection = 'right';
      isMoving = true;
    }

    if (this.cursors.up.isDown || keys.W.isDown) {
      body.setVelocityY(-this.playerSpeed);
      newDirection = 'up';
      isMoving = true;
    } else if (this.cursors.down.isDown || keys.S.isDown) {
      body.setVelocityY(this.playerSpeed);
      newDirection = 'down';
      isMoving = true;
    }

    // Normalize diagonal
    if (body.velocity.x !== 0 && body.velocity.y !== 0) {
      body.velocity.normalize().scale(this.playerSpeed);
    }

    // Update animation
    if (isMoving) {
      const walkAnim = `${this.charId}-walk-${newDirection}`;
      if (newDirection !== this.lastDirection || !this.player.anims.isPlaying) {
        if (this.anims.exists(walkAnim)) {
          this.player.play(walkAnim, true);
        } else {
          // Fallback to player-walk for non-atlas characters
          this.player.play(`player-walk-${newDirection}`, true);
        }
        this.lastDirection = newDirection;
      }
    } else {
      if (this.player.anims.isPlaying) {
        this.player.stop();
        // Play idle animation
        const idleAnim = `${this.charId}-idle`;
        if (this.anims.exists(idleAnim)) {
          this.player.play(idleAnim);
        }
      }
    }
  }
}
